/**
 * mathConfig.ts — loads & types the raw math/config.json that drives the mock.
 *
 * Mirrors the 3-layer model exactly:
 *   LAYER 1 drip          — per-color total drip (multiple of color stake S)
 *   LAYER 2 color-complete— bonus on last cell (always >0 stardust floor)
 *   LAYER 3 picture-bonus — multiple of TOTAL stake, * (done/K)^1.5
 */
import raw from '../assets/math-config.json';
import type {
  ColorCompleteDisplayRow,
  DripDisplayRow,
  MathConfig,
  PictureCompleteDisplayRow,
} from './contract';

interface RawWeighted {
  weight: number;
  [k: string]: number;
}

interface RawConfig {
  rtp_target: number;
  split: { r_c: number; beta: number };
  bet_levels: number[];
  picture_specs: {
    name: string;
    num_colors: number;
    total_cells: number;
    cells_per_color: number[];
  }[];
  per_color: {
    drip_table: { mult_of_stake: number; weight: number }[];
    color_complete_table: { mult_of_stake: number; weight: number }[];
    stardust_floor_mult: number;
    expected_drip_fraction: number;
    expected_color_complete_fraction: number;
  };
  picture_complete_bonus: {
    table: { mult_of_total_stake: number; weight: number }[];
    coverage_exponent: number;
    max_mult: number;
    expected_fraction_of_total_stake: number;
  };
  rtp_decomposition: { drip: number; color_complete: number; picture_complete: number };
}

const cfg = raw as unknown as RawConfig;

export interface WeightedTable {
  mults: number[];
  weights: number[];
  total: number;
}

function table(rows: { weight: number }[], key: string): WeightedTable {
  const mults: number[] = [];
  const weights: number[] = [];
  let total = 0;
  for (const r of rows as RawWeighted[]) {
    mults.push(r[key]);
    weights.push(r.weight);
    total += r.weight;
  }
  return { mults, weights, total };
}

export const DRIP_TABLE = table(cfg.per_color.drip_table, 'mult_of_stake');
export const COLOR_COMPLETE_TABLE = table(
  cfg.per_color.color_complete_table,
  'mult_of_stake',
);
export const PICTURE_TABLE = table(
  cfg.picture_complete_bonus.table,
  'mult_of_total_stake',
);

export const STARDUST_FLOOR = cfg.per_color.stardust_floor_mult;
export const COVERAGE_EXPONENT = cfg.picture_complete_bonus.coverage_exponent;
export const MAX_PIC_MULT = cfg.picture_complete_bonus.max_mult;
export const BET_LEVELS = cfg.bet_levels;
export const PICTURE_SPECS = cfg.picture_specs;
export const RTP = cfg.rtp_target;
export const SPLIT = cfg.split;

function toDisplay(t: WeightedTable): { mult: number; weightPct: number }[] {
  return t.mults.map((m, i) => ({
    mult: m,
    weightPct: (t.weights[i] / t.total) * 100,
  }));
}

export function buildMathConfig(): MathConfig {
  return {
    rtp: cfg.rtp_target,
    split: cfg.split,
    dripDisplay: toDisplay(DRIP_TABLE) as DripDisplayRow[],
    colorCompleteDisplay: toDisplay(COLOR_COMPLETE_TABLE) as ColorCompleteDisplayRow[],
    pictureCompleteDisplay: toDisplay(PICTURE_TABLE) as PictureCompleteDisplayRow[],
    betLevels: cfg.bet_levels,
  };
}

export const MATH_VERSION = 'colordrop-1.0.0';
